import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import MosaicWall from "../../components/mosaic/MosaicWall";
import "./game.css";

const ZOOM_MIN = 0.75;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
const MOBILE_MQ = "(max-width: 768px)";

function clampZoom(z) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));
}

function touchDistance(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

export default function GamePage() {
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const [zoom, setZoom] = useState(1);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const viewportRef = useRef(null);
  const pinchRef = useRef(null);
  const zoomRef = useRef(zoom);

  const titleColor = themeData?.text_color || "#1a2b6d";

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  // Keep mosaic settings (colors, words, mode) fresh after admin changes.
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const zoomIn = useCallback(() => {
    setZoom((z) => clampZoom(z + ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => clampZoom(z - ZOOM_STEP));
  }, []);

  const zoomReset = useCallback(() => setZoom(1), []);

  // Pinch-to-zoom on the mosaic viewport (mobile / touch).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (e.touches.length !== 2) return;
      pinchRef.current = {
        startDist: touchDistance(e.touches[0], e.touches[1]),
        startZoom: zoomRef.current,
      };
    };

    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || !pinchRef.current) return;
      e.preventDefault();
      const dist = touchDistance(e.touches[0], e.touches[1]);
      const { startDist, startZoom } = pinchRef.current;
      if (!startDist) return;
      setZoom(clampZoom(startZoom * (dist / startDist)));
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchRef.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="cg-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          Could not load game data.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="cg-submit"
          style={{ margin: "16px auto", display: "block" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="cg-page cg-page--game">
      {isMobile ? (
        <div className="cg-zoom-bar" role="toolbar" aria-label="Mosaic zoom">
          <button
            type="button"
            className="cg-zoom-btn"
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
            aria-label="Zoom out"
            title="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            className="cg-zoom-pct"
            onClick={zoomReset}
            aria-label="Reset zoom"
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="cg-zoom-btn"
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
            aria-label="Zoom in"
            title="Zoom in"
          >
            +
          </button>
        </div>
      ) : null}

      <div className="cg-mosaic-viewport" ref={viewportRef}>
        {/*
          Use CSS `zoom` (not transform: scale) so the letter mask keeps clipping
          player words inside the outline when zoomed.
        */}
        <div className="cg-mosaic-zoom-layer" style={{ zoom }}>
          <MosaicWall />
        </div>
      </div>
    </div>
  );
}
